import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { updateUser } = useAuth();
    const [state, setState] = useState({ phase: "checking" });

    useEffect(() => {
        let cancelled = false;
        const accept = async () => {
            try {
                const { data } = await api.post("/auth/accept-invite", { token });
                if (cancelled) return;
                if (data.access_token) localStorage.setItem("bula_token", data.access_token);
                updateUser(data.user);
                setState({ phase: "success", jv_id: data.jv_id, user: data.user });
            } catch (e) {
                if (cancelled) return;
                setState({ phase: "error", message: formatApiError(e.response?.data?.detail) || e.message });
            }
        };
        accept();
        return () => { cancelled = true; };
    }, [token, updateUser]);

    return (
        <div data-testid="accept-invite-page" className="max-w-xl mx-auto px-4 py-20">
            <Card className="p-10 text-center">
                {state.phase === "checking" && (
                    <>
                        <Loader2 className="h-12 w-12 mx-auto text-coral animate-spin mb-4" />
                        <h1 className="font-display text-2xl font-bold">Activating your account…</h1>
                    </>
                )}
                {state.phase === "success" && (
                    <>
                        <Sparkles className="h-5 w-5 mx-auto text-coral mb-2" />
                        <div className="h-16 w-16 rounded-full bg-fiji-green/20 grid place-items-center mx-auto mb-4">
                            <CheckCircle2 className="h-10 w-10 text-fiji-green" />
                        </div>
                        <h1 className="font-display text-3xl font-bold mb-2">Welcome, {state.user?.full_name?.split(" ")[0] || "there"} 🌺</h1>
                        <p className="font-app text-muted-foreground mb-6">
                            Your seller account is active. We've created a draft Joint Venture submission based on the details you shared — review it and let our deal team know what to refine.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <Button onClick={() => navigate("/dashboard")} data-testid="invite-dashboard-btn" className="bg-ocean-green text-white">
                                Go to my dashboard <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            {state.jv_id && (
                                <Button asChild variant="outline">
                                    <Link to={`/jv/${state.jv_id}`}>View draft Joint Venture</Link>
                                </Button>
                            )}
                        </div>
                    </>
                )}
                {state.phase === "error" && (
                    <>
                        <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
                        <h1 className="font-display text-2xl font-bold mb-2">This invite is no longer valid</h1>
                        <p className="font-app text-muted-foreground mb-6">{state.message}</p>
                        <Button asChild className="bg-ocean-green text-white"><Link to="/login">Sign in instead</Link></Button>
                    </>
                )}
            </Card>
        </div>
    );
}
